import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Spin, 
  Divider, 
  InputNumber, 
  Tabs, 
  Select,
  Upload,
  Space,
  Typography,
  Tooltip,
} from 'antd';
import { 
  SaveOutlined, 
  ReloadOutlined, 
  UploadOutlined, 
  DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload';
import { getConfigList, updateConfig } from '../../services/config';
import { getAllCampuses } from '../../services/campus';
import { getAllCampusSealConfigs, updateCampusSealConfig, uploadCampusSealImage, getAllCampusReportConfigs, updateCampusReportConfig, uploadCampusImage } from '../../services/campusConfig';
import { SystemConfig, Campus } from '../../types';
import '../../styles/admin-pages.css';
import './Settings.css';

const { Text } = Typography;

const Settings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [sealConfigs, setSealConfigs] = useState<Record<string, { sealText: string; sealImagePath: string }>>({});
  const [activeTab, setActiveTab] = useState('system');
  const [selectedCampus, setSelectedCampus] = useState<string>('');
  const [sealLoading, setSealLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [sealForm] = Form.useForm();
  const [reportForm] = Form.useForm();
  const [reportConfigs, setReportConfigs] = useState<Record<string, any>>({});
  const [reportCampus, setReportCampus] = useState<string>('');
  const [reportSaving, setReportSaving] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [badgeImageUrl, setBadgeImageUrl] = useState('');
  const [schoolNameImageUrl, setSchoolNameImageUrl] = useState('');
  const [signatureImageUrl, setSignatureImageUrl] = useState('');

  useEffect(() => {
    fetchConfigs();
    fetchCampuses();
    fetchSealConfigs();
    fetchReportConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await getConfigList();
      const configValues: Record<string, string> = {};
      res.forEach((item: SystemConfig) => {
        configValues[item.configKey] = item.configValue;
      });
      form.setFieldsValue(configValues);
    } catch (error) {
      message.error('获取系统配置失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampuses = async () => {
    try {
      const res = await getAllCampuses();
      setCampuses(res || []);
      if (res && res.length > 0) {
        setSelectedCampus(res[0].id);
      }
    } catch (error) {
      message.error('获取校区列表失败');
    }
  };

  const fetchSealConfigs = async () => {
    try {
      const res = await getAllCampusSealConfigs();
      const configs: Record<string, { sealText: string; sealImagePath: string }> = {};
      res.forEach((item: any) => {
        configs[item.campusId] = {
          sealText: item.sealText,
          sealImagePath: item.sealImagePath,
        };
      });
      setSealConfigs(configs);
    } catch (error) {
      message.error('获取校区公章配置失败');
    }
  };

  const fetchReportConfigs = async () => {
    setReportLoading(true);
    try {
      const res = await getAllCampusReportConfigs();
      const configs: Record<string, any> = {};
      res.forEach((item: any) => { configs[item.campusId] = item; });
      setReportConfigs(configs);
      if (res.length > 0 && !reportCampus) setReportCampus(res[0].campusId);
    } catch (error) {
      message.error('获取报告配置失败');
    } finally {
      setReportLoading(false);
    }
  };


  const handleSaveReport = async (values: any) => {
    if (!reportCampus) { message.warning('请选择校区'); return; }
    setReportSaving(true);
    try {
      await updateCampusReportConfig(reportCampus, {
        badgeImagePath: badgeImageUrl,
        schoolNameImagePath: schoolNameImageUrl,
        contactEmail: values.contactEmail,
        contactAddress: values.contactAddress,
        contactPhone: values.contactPhone,
        signatureImagePath: signatureImageUrl,
      });
      message.success('报告配置保存成功');
      await fetchReportConfigs();
    } catch { message.error('保存失败'); }
    finally { setReportSaving(false); }
  };

  const handleSaveSystem = async (values: any) => {
    setSaving(true);
    try {
      for (const [key, value] of Object.entries(values)) {
        await updateConfig(key, String(value));
      }
      message.success('系统配置保存成功');
    } catch (error) {
      message.error('配置保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeal = async (values: any) => {
    if (!selectedCampus) {
      message.error('请先选择校区');
      return;
    }
    setSealLoading(true);
    try {
      await updateCampusSealConfig(selectedCampus, {
        sealText: values.sealText,
        sealImagePath: values.sealImagePath,
      });
      message.success('校区公章配置保存成功');
      fetchSealConfigs();
    } catch (error) {
      message.error('公章配置保存失败');
    } finally {
      setSealLoading(false);
    }
  };

  const currentSealConfig = sealConfigs[selectedCampus] || { sealText: '', sealImagePath: '' };

  // 处理图片URL，确保可以正确显示
  const getImageUrl = (path: string): string => {
    if (!path) return '';
    // 已经是完整URL
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // 相对路径拼接后端地址
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const apiBase = baseUrl.replace('/api', '');
    return `${apiBase}${path.startsWith('/') ? path : '/' + path}`;
  };

  // 构建预览报告 URL（直接指向后端，避免前端路由拦截）
  const getPreviewUrl = (campusId: string) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const apiBase = baseUrl.replace('/api', '');
    return `${apiBase}/api/reports/preview?campusId=${campusId}`;
  };

  // 更新公章表单值当选择校区变化时
  useEffect(() => {
    if (selectedCampus && sealConfigs[selectedCampus]) {
      sealForm.setFieldsValue({
        sealText: sealConfigs[selectedCampus].sealText,
        sealImagePath: sealConfigs[selectedCampus].sealImagePath,
      });
      setPreviewImage(getImageUrl(sealConfigs[selectedCampus].sealImagePath));
    } else {
      sealForm.resetFields();
      setPreviewImage('');
    }
  }, [selectedCampus, sealConfigs, sealForm]);

  // 处理公章图片上传
  const handleSealUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;
    
    // 验证文件类型
    const isPng = (file as File).type === 'image/png';
    if (!isPng) {
      message.error('只能上传 PNG 格式的图片！');
      onError?.(new Error('Invalid file type'));
      return;
    }
    
    // 验证文件大小 (最大 2MB)
    const isLt2M = (file as File).size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB！');
      onError?.(new Error('File too large'));
      return;
    }

    setUploadLoading(true);
    try {
      const res = await uploadCampusSealImage(file as File);
      sealForm.setFieldsValue({ sealImagePath: res.url });
      setPreviewImage(res.url);
      onSuccess?.(res);
      message.success('公章图片上传成功');
    } catch (error) {
      onError?.(error as Error);
      message.error('上传失败');
    } finally {
      setUploadLoading(false);
    }
  };

  // 删除公章图片
  const handleDeleteSealImage = () => {
    sealForm.setFieldsValue({ sealImagePath: '' });
    setPreviewImage('');
    message.success('图片已删除');
  };

  return (
    <div>
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'system',
            label: '系统配置',
            forceRender: true,
            children: (
              <Card
                title="全局系统配置"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={fetchConfigs} disabled={loading}>
                    刷新
                  </Button>
                }
                className="admin-page-card"
              >
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSaveSystem}
                  style={{ maxWidth: 600 }}
                  className="admin-form"
                >
                  <Divider>基本信息</Divider>
                  <Form.Item
                    name="system_title"
                    label={
                      <span>
                        系统标题
                        <Tooltip title="显示在浏览器标签页和系统Logo旁的标题">
                          <InfoCircleOutlined style={{ marginLeft: 8, color: '#94a3b8' }} />
                        </Tooltip>
                      </span>
                    }
                    rules={[
                      { required: true, message: '请输入系统标题' },
                      { max: 30, message: '系统标题不能超过30个字符' },
                      { pattern: /^[\u4e00-\u9fa5a-zA-Z0-9\s\-_.]+$/, message: '标题只能包含中文、英文、数字和常见符号' },
                    ]}
                  >
                    <Input 
                      placeholder="例如：某某大学成绩查询系统" 
                      maxLength={30}
                      showCount
                    />
                  </Form.Item>

                  <Divider>报告设置</Divider>
                  <Form.Item
                    name="report_default_validity_days"
                    label="默认报告有效期（天）"
                    rules={[{ required: true, message: '请输入默认有效期' }]}
                  >
                    <InputNumber min={1} max={365} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="report_min_validity_days"
                    label="最小报告有效期（天）"
                    rules={[{ required: true, message: '请输入最小有效期' }]}
                  >
                    <InputNumber min={1} max={365} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="report_max_validity_days"
                    label="最大报告有效期（天）"
                    rules={[{ required: true, message: '请输入最大有效期' }]}
                  >
                    <InputNumber min={1} max={365} style={{ width: '100%' }} />
                  </Form.Item>

                  <Divider>安全设置</Divider>
                  <Form.Item
                    name="login_max_failures"
                    label="登录最大失败次数"
                    rules={[{ required: true, message: '请输入最大失败次数' }]}
                  >
                    <InputNumber min={1} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="login_lock_duration_minutes"
                    label="登录锁定时间（分钟）"
                    rules={[{ required: true, message: '请输入锁定时间' }]}
                  >
                    <InputNumber min={1} max={1440} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="captcha_expiry_seconds"
                    label="验证码有效期（秒）"
                    rules={[{ required: true, message: '请输入验证码有效期' }]}
                  >
                    <InputNumber min={30} max={600} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item style={{ marginTop: 24 }}>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} className="admin-btn-primary">
                      保存配置
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            ),
          },
          {
            key: 'seal',
            label: '校区公章配置',
            forceRender: true,
            children: (
              <Card
                title="校区电子公章配置"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={fetchSealConfigs} loading={sealLoading}>
                    刷新
                  </Button>
                }
                className="admin-page-card"
              >
                <Form 
                  form={sealForm}
                  layout="vertical" 
                  onFinish={handleSaveSeal} 
                  style={{ maxWidth: 600 }}
                  className="admin-form"
                >
                  <Form.Item label="选择校区" required>
                    <Select
                      value={selectedCampus}
                      onChange={setSelectedCampus}
                      placeholder="请选择校区"
                      style={{ width: '100%' }}
                    >
                      {campuses.map((campus) => (
                        <Select.Option key={campus.id} value={campus.id}>
                          {campus.campusName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {selectedCampus && (
                    <>
                      <Divider>公章设置</Divider>
                      <Form.Item
                        name="sealText"
                        label="签章文字"
                        rules={[
                          { required: true, message: '请输入签章文字' },
                          { max: 50, message: '签章文字不能超过50个字符' },
                        ]}
                      >
                        <Input 
                          placeholder="例如：国际学院A校区成绩验证专用章" 
                          maxLength={50}
                          showCount
                        />
                      </Form.Item>

                      <Form.Item
                        name="sealImagePath"
                        label="签章图片"
                        rules={[{ required: true, message: '请上传签章图片' }]}
                        extra={
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            支持 PNG 格式，建议尺寸 200x200 像素，文件大小不超过 2MB
                          </Text>
                        }
                      >
                        <Input type="hidden" />
                      </Form.Item>

                      {/* 图片上传区域 */}
                      <div className="seal-upload-section">
                        {previewImage ? (
                          <div className="seal-preview-container">
                            <div className="seal-preview-image-wrapper">
                              <img 
                                src={previewImage} 
                                alt="公章预览" 
                                className="seal-preview-image"
                              />
                            </div>
                            <Space className="seal-preview-actions">
                              <Upload
                                accept=".png"
                                showUploadList={false}
                                customRequest={handleSealUpload}
                                disabled={uploadLoading}
                              >
                                <Button icon={<UploadOutlined />} loading={uploadLoading}>
                                  替换图片
                                </Button>
                              </Upload>
                              <Button 
                                icon={<DeleteOutlined />} 
                                danger 
                                onClick={handleDeleteSealImage}
                              >
                                删除
                              </Button>
                            </Space>
                          </div>
                        ) : (
                          <Upload.Dragger
                            accept=".png"
                            showUploadList={false}
                            customRequest={handleSealUpload}
                            disabled={uploadLoading}
                            className="seal-uploader"
                          >
                            <p className="ant-upload-drag-icon">
                              <UploadOutlined style={{ fontSize: 48, color: '#4F46E5' }} />
                            </p>
                            <p className="ant-upload-text">点击或拖拽上传公章图片</p>
                            <p className="ant-upload-hint">
                              仅支持 PNG 格式，文件大小不超过 2MB
                            </p>
                          </Upload.Dragger>
                        )}
                      </div>

                      <Form.Item style={{ marginTop: 24 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          icon={<SaveOutlined />}
                          loading={sealLoading}
                          className="admin-btn-primary"
                        >
                          保存校区公章配置
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form>
              </Card>
            ),
          },
          {
            key: 'report',
            label: '报告自定义',
            forceRender: true,
            children: (
              <Card
                title="成绩验证报告自定义配置"
                extra={
                  <Space>
                    <Button onClick={fetchReportConfigs} loading={reportLoading}>刷新</Button>
                    <Button type="primary" onClick={() => {
                      if (!reportCampus) { message.warning('请先选择校区'); return; }
                      window.open(getPreviewUrl(reportCampus), '_blank');
                    }}>
                      预览报告
                    </Button>
                  </Space>
                }
                className="admin-page-card"
              >
                <Spin spinning={reportLoading}>
                  <Form form={reportForm} layout="vertical" onFinish={handleSaveReport} style={{ maxWidth: 600 }} className="admin-form">
                    <Form.Item label="选择校区" required>
                      <Select value={reportCampus} onChange={(v) => {
                        setReportCampus(v);
                        const cfg = reportConfigs[v];
                        if (cfg) {
                          reportForm.setFieldsValue(cfg);
                          setBadgeImageUrl(cfg.badgeImagePath || '');
                          setSchoolNameImageUrl(cfg.schoolNameImagePath || '');
                          setSignatureImageUrl(cfg.signatureImagePath || '');
                        } else {
                          reportForm.resetFields();
                          setBadgeImageUrl('');
                          setSchoolNameImageUrl('');
                          setSignatureImageUrl('');
                        }
                      }} placeholder="请选择校区" style={{ width: '100%' }}>
                        {campuses.map((c) => <Select.Option key={c.id} value={c.id}>{c.campusName}</Select.Option>)}
                      </Select>
                    </Form.Item>

                    {reportCampus && (
                      <>
                        <Divider>校徽与校名</Divider>
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ marginBottom: 4, color: '#666', fontSize: 13 }}>校徽图片（PNG）</div>
                          <Upload
                            accept="image/png"
                            showUploadList={false}
                            beforeUpload={async (file) => {
                              try { const res = await uploadCampusImage(file, 'badge'); setBadgeImageUrl(res.url); message.success('校徽上传成功'); } catch { message.error('上传失败'); }
                              return false;
                            }}
                          >
                            <Button icon={<UploadOutlined />}>上传校徽</Button>
                          </Upload>
                          {badgeImageUrl && <img src={badgeImageUrl} alt="校徽" style={{ height: 60, marginTop: 8, display: 'block' }} />}
                        </div>
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ marginBottom: 4, color: '#666', fontSize: 13 }}>校名图片（PNG）</div>
                          <Upload
                            accept="image/png"
                            showUploadList={false}
                            beforeUpload={async (file) => {
                              try { const res = await uploadCampusImage(file, 'school_name'); setSchoolNameImageUrl(res.url); message.success('校名图片上传成功'); } catch { message.error('上传失败'); }
                              return false;
                            }}
                          >
                            <Button icon={<UploadOutlined />}>上传校名图片</Button>
                          </Upload>
                          {schoolNameImageUrl && <img src={schoolNameImageUrl} alt="校名" style={{ height: 40, marginTop: 8, display: 'block' }} />}
                        </div>

                        <Divider>联系方式</Divider>
                        <Form.Item name="contactEmail" label="验证邮箱"><Input placeholder="如：verify@university.edu.cn" /></Form.Item>
                        <Form.Item name="contactAddress" label="大学地址"><Input placeholder="如：XX省XX市XX路XX号" /></Form.Item>
                        <Form.Item name="contactPhone" label="联系电话"><Input placeholder="如：0591-12345678" /></Form.Item>

                        <Divider>负责人签字</Divider>
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ marginBottom: 4, color: '#666', fontSize: 13 }}>签名图片（PNG）</div>
                          <Upload
                            accept="image/png"
                            showUploadList={false}
                            beforeUpload={async (file) => {
                              try { const res = await uploadCampusImage(file, 'signature'); setSignatureImageUrl(res.url); message.success('签名上传成功'); } catch { message.error('上传失败'); }
                              return false;
                            }}
                          >
                            <Button icon={<UploadOutlined />}>上传签名图片</Button>
                          </Upload>
                          {signatureImageUrl && <img src={signatureImageUrl} alt="签名" style={{ height: 50, marginTop: 8, display: 'block' }} />}
                        </div>

                        <Form.Item style={{ marginTop: 24 }}>
                          <Space>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={reportSaving} className="admin-btn-primary">保存配置</Button>
                            <Button type="default" onClick={() => {
                              if (!reportCampus) { message.warning('请先选择校区'); return; }
                              window.open(getPreviewUrl(reportCampus), '_blank');
                            }}>预览报告</Button>
                          </Space>
                        </Form.Item>
                      </>
                    )}
                  </Form>
                </Spin>
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default Settings;
